import { Link, useNavigate } from 'react-router-dom'
import { ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useFeedControlsSummary } from '@/hooks/useControls'
import { Sheet } from './ui'
import '@/styles/controls.css'

/**
 * Feed controls (Brian's after/feed-controls.js, Oct 22): one sheet with the
 * four things that shape Your feed, each saying how it's set now and opening
 * its screen; each screen comes back here ("‹ Feed controls").
 *
 *   <FeedControlsSheet open onClose />   the sheet, over whatever is behind it
 *   <TunedFor />                         the "Tuned for Bend / + 2 places · 6 topics · 3 watches"
 *                                        line for under the feed's title; opens the sheet
 *
 * /app/feed-controls shows Your feed with this sheet open over it.
 */

const ICON = {
  topics: <><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></>,
  places: <><path d="M12 21s-6-5.6-6-11a6 6 0 0 1 12 0c0 5.4-6 11-6 11z" /><circle cx="12" cy="10" r="2.2" /></>,
  breaking: <><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></>,
  records: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
}

export function FeedControlsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const s = useFeedControlsSummary()
  const desktop = useIsDesktop()
  const rows = [
    { to: ROUTES.topics, ic: ICON.topics, name: 'Topics', what: 'What kinds of stories, and how much of each', now: s.topicsLine },
    { to: ROUTES.places, ic: ICON.places, name: 'Places', what: 'The towns and counties you cover', now: s.placesLine },
    { to: ROUTES.breaking, ic: ICON.breaking, name: 'Breaking news', what: 'Stories you want the moment they happen', now: s.breakingLine },
    { to: ROUTES.recordsSettings, ic: ICON.records, name: 'Public records', what: 'Permits, licenses and agendas for your places', now: s.recordsLine },
  ]
  return (
    <Sheet open={open} onClose={onClose} label="Feed controls" className="fx-fc">
      <div className="hd">
        <h3>Feed controls</h3>
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <p className="sub">What shows up in Your feed. Changes start with your next stories.</p>
      <div className="ls">
        {rows.map((r) => (
          <Link key={r.to} className="r" to={r.to}>
            <span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true">{r.ic}</svg></span>
            <span className="t"><b>{r.name}</b><small>{r.what}</small><em>{r.now}</em></span>
            <span className="ch" aria-hidden="true" />
          </Link>
        ))}
        <p className="foot">
          {desktop
            ? 'Your account, plan and seeds are in Settings (your account card, bottom left).'
            : 'Your account, plan and seeds are in Settings (your initials, top right).'}
        </p>
      </div>
    </Sheet>
  )
}

/** "Tuned for Bend" with "+ 2 places · 6 topics · 3 watches" under it (Brian:
 *  the home place on the first line, the rest below). Opens Feed controls. */
export function TunedFor({ className }: { className?: string }) {
  const { tuned, ready } = useFeedControlsSummary()
  const navigate = useNavigate()
  return (
    <button type="button" className={`fx-tuned${className ? ` ${className}` : ''}`} aria-label="Feed controls: tune what shows up here"
      onClick={() => navigate(ROUTES.feedControls)}>
      <span>
        {ready ? <><span className="l1">{tuned.l1}</span><span className="l2">{tuned.l2}</span></> : <span className="l1">Tune what shows up here</span>}
      </span>
      <b><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>Tune</b>
    </button>
  )
}
